/*!
 * Copyright 2022 Thorne Health Lab. All rights reserved.
 */
const DEFAULT_CLONE_DEPTH = 20

// ─────────────────────────────────  MAIN  ────────────────────────────────────

/**
 * Returns an object containing all elements that differ between two objects.
 *
 * @template {object} T
 * @param {T} object1  The base object to compare to
 * @param {T} object2  The object to compare with
 * @param {number} [depth=DEFAULT_CLONE_DEPTH]  An optional depth to prevent recursion.  Default: 20.
 * @return {T | {}} A differential object, which if extended onto object1 would result in object2.
 */

module.exports = function diffDeep(
  object1,
  object2,
  depth = DEFAULT_CLONE_DEPTH
) {
  if (depth < 0) return {}

  /** @type {object} */
  var diff = {}
  // Process each element from object2, adding any element that's different
  // from object 1.
  for (var parm in object2) {
    var value1 = object1[parm]
    var value2 = object2[parm]
    if (value1 && value2 && isObject(value2)) {
      if (!equalsDeep(value1, value2)) {
        diff[parm] = diffDeep(value1, value2, depth - 1)
      }
    } else if (Array.isArray(value1) && Array.isArray(value2)) {
      if (!equalsDeep(value1, value2)) {
        diff[parm] = value2
      }
    } else if (value1 !== value2) {
      diff[parm] = value2
    }
  }

  // Return the diff object
  return diff
}
// ────────────────────────────────  private  ──────────────────────────────────

function isObject(obj) {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj)
}

function equalsDeep(object1, object2, depth = DEFAULT_CLONE_DEPTH) {
  if (depth < 0) return {}

  // Fast comparisons
  if (!object1 || !object2) {
    return false
  }
  if (object1 === object2) {
    return true
  }
  if (typeof object1 !== 'object' || typeof object2 !== 'object') {
    return false
  }

  // They must have the same keys.  If their length isn't the same
  // then they're not equal.  If the keys aren't the same, the value
  // comparisons will fail.
  if (Object.keys(object1).length !== Object.keys(object2).length) {
    return false
  }

  // Compare the values
  for (var prop in object1) {
    // Call recursively if an object or array
    if (object1[prop] && typeof object1[prop] === 'object') {
      if (!equalsDeep(object1[prop], object2[prop], depth - 1)) {
        return false
      }
    } else {
      if (object1[prop] !== object2[prop]) {
        return false
      }
    }
  }

  // Test passed.
  return true
}
